import client from './client';
import { geospatialApi } from './geospatialApi';
import { breedEcotypeApi, productionPurposeApi, speciesApi, ageClassApi } from './speciesLivestockRegistryApi';
import { NIGERIAN_STATES } from '../component/Admin/data/enums';

// Fallbacks used when the backend endpoint returns nothing or fails
const FALLBACK_PRODUCTION_SYSTEMS = [
  { value: 'pastoral',        label: 'Pastoral' },
  { value: 'agropastoral',    label: 'Agropastoral' },
  { value: 'intensive',       label: 'Intensive' },
  { value: 'semi_intensive',  label: 'Semi-Intensive' },
  { value: 'backyard',        label: 'Backyard' },
  { value: 'nomadic',         label: 'Nomadic' },
];

const FALLBACK_COUNT_METHODS = [
  { value: 'physical_count', label: 'Physical Count' },
  { value: 'naits_registry', label: 'NAITS Registry' },
  { value: 'lga_records',    label: 'LGA Records' },
  { value: 'estimate',       label: 'Estimate' },
  { value: 'aerial_survey',  label: 'Aerial Survey' },
];

const FALLBACK_DATA_SOURCES = [
  { value: 'fmld_field_count', label: 'FMLD Field Count' },
  { value: 'lga_report',       label: 'LGA Report' },
  { value: 'state_adp',        label: 'State ADP' },
  { value: 'nbs_survey',       label: 'NBS Survey' },
  { value: 'estimate',         label: 'Estimate' },
];

const VALIDATION_STATUSES = [
  { value: 'draft',     label: 'Draft' },
  { value: 'submitted', label: 'Submitted' },
  { value: 'validated', label: 'Validated' },
  { value: 'approved',  label: 'Approved' },
  { value: 'rejected',  label: 'Rejected' },
];

async function tryFetchOptions(path) {
  try {
    const { data } = await client.get(path, { params: { per_page: 500 } });
    const payload = data?.data ?? data ?? {};
    const items =
      Array.isArray(payload) ? payload :
      Array.isArray(payload.items) ? payload.items :
      Array.isArray(payload.results) ? payload.results :
      [];
    return items
      .map((item) => ({
        value: item.code ?? item.value ?? item.name ?? item.uuid,
        label: item.name ?? item.label ?? item.code ?? item.uuid,
      }))
      .filter((item) => item.value);
  } catch {
    return [];
  }
}

function extractStateStrings(payload) {
  const items = Array.isArray(payload) ? payload :
    Array.isArray(payload?.items) ? payload.items :
    Array.isArray(payload?.results) ? payload.results : [];
  return items.map((item) =>
    typeof item === 'string' ? item : (item?.value ?? item?.state ?? item?.name ?? '')
  ).filter(Boolean);
}

export async function loadPopulationRefData() {
  const [statesResult, speciesResult, breedResult, productionPurposeResult, ageClassResult, prodSysResult, countMethodResult, dataSourceResult] =
    await Promise.allSettled([
      geospatialApi.states(),
      speciesApi.listAll({ per_page: 200 }),
      breedEcotypeApi.listAll({ per_page: 200 }),
      productionPurposeApi.listAll({ per_page: 200 }),
      ageClassApi.listAll({ per_page: 200 }),
      tryFetchOptions('/v1/production-systems'),
      tryFetchOptions('/v1/count-methods'),
      tryFetchOptions('/v1/data-sources'),
    ]);

  const rawStates = statesResult.status === 'fulfilled' ? statesResult.value : null;
  const stateStrings = rawStates ? extractStateStrings(rawStates) : [];

  return {
    states: stateStrings.length > 0 ? stateStrings : NIGERIAN_STATES.map((s) => s.value),
    species: speciesResult.status === 'fulfilled' ? (speciesResult.value?.items ?? []) : [],
    breeds: breedResult.status === 'fulfilled' ? (breedResult.value?.items ?? []) : [],
    productionPurposes: productionPurposeResult.status === 'fulfilled' ? (productionPurposeResult.value?.items ?? []) : [],
    ageClasses: ageClassResult.status === 'fulfilled' ? (ageClassResult.value?.items ?? []) : [],
    productionSystems:
      prodSysResult.status === 'fulfilled' && prodSysResult.value.length > 0
        ? prodSysResult.value
        : FALLBACK_PRODUCTION_SYSTEMS,
    countMethods:
      countMethodResult.status === 'fulfilled' && countMethodResult.value.length > 0
        ? countMethodResult.value
        : FALLBACK_COUNT_METHODS,
    dataSources:
      dataSourceResult.status === 'fulfilled' && dataSourceResult.value.length > 0
        ? dataSourceResult.value
        : FALLBACK_DATA_SOURCES,
    validationStatuses: VALIDATION_STATUSES,
  };
}
